import { openWithTenant } from '@app-store'
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form'
import {
  FormRendererProps,
  LayoutRendererProps,
  ViewRendererProps,
} from '../types/formRenderer.types'
import RowRenderer from './RowRenderer'
import { RowRender } from './MultiLookupRowRender'
import {
  AUTO_SCROLL_OFFSET_PX,
  componentNames,
  MAXRTE_AUTO_SCROLL_TIMEOUT,
  MAXRTE_SUPPORTED_TABLE_LAYOUT_IDS,
  MODE_CONSTANTS,
  NAMESPACES,
  ROW_TYPES,
  SUPPORTED_NESTING_DEPTH,
  TASK_STATUS,
} from '../utils/constants'
import {
  formatDataBeforeLoad,
  formatValueForViewMode,
  getSiblingElementKeyName,
  isDraftSubmission,
  isThisAParentCaseIdHyperLink,
  removeAuditFieldsAndFormatValues,
  removePhoneMasking,
  setDefaultValueForDropdown,
} from '../utils/form.utils'
import { Tooltip } from '@ui-kit'
import { hideUnauthorizedData } from '../utils/permission'
import ThreadsNotesAccordion from './ThreadsNotesAccordion'
import { v4 as uuid } from 'uuid'
import SubFormRenderer from './SubFormRenderer'
import { AdditionalNotes } from './AdditionalNotes'
import { openNewWindow } from '../utils/helperFunctions'
import { useAppDispatch, useAppSelector } from '../utils/agent.store'

import { generateTabIndexMap } from '../utils/tab-indexing'
import { TabIndexMap } from '../types/common'
import { ShortcutKeys } from '../constants/form.constants'
import {
  SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT,
  addToaster,
  registerFormInstance,
  setDialPad,
  unregisterFormInstance,
  updateMaxRTEShouldAutoScroll,
} from '@app-store'
import { entitiesRoute, viewRoute } from '../routes/route.constant'
import { evaluateFormula } from '../utils/formula.utils'
import {
  RenderNestedComponentPropsType,
  ShowComputedValuePropsType,
  // ShowHyperLinkPropsType,
} from '../types/types'
import { isEmpty, merge } from 'lodash'
import { EntityLayout } from '../types/layout.types'
import { fetchDownloadUrlForSingleFIles } from '../apis/fetchPresignedUrl.api'
import { getMigrationStatus } from '../utils/migration.utils'
import FormHeaderForMaxRTE from './max-rte/FormHeaderForMaxRTE'
import { calculateAnnualAmountFromFrequency } from '../utils/hybridFormRendererfuntions.utils'
import SubFormRenderWrapper from './SubFormRenderWrapper'
import { FormBehaviorRunner } from '../form-behavior/FormBehaviorRunner'
import { triggerFormBehaviorEvaluation } from '../form-behavior/formBehaviorTrigger.registry'
import {
  registerFormAncestryGetValues,
  unregisterFormAncestryGetValues,
} from '../form-behavior/formAncestry.registry'

export const ViewRenderer = <T extends FieldValues>({
  element,
  tabIndex,
  control,
  layoutId,
  layout,
  task,
}: ViewRendererProps<T>) => {
  let keyName = element.keyName
  // const workflowDefinition = useAppSelector(
  //   (state) => state?.workflowData?.workflowDefinition
  // )
  const watchedValue = useWatch({ control })
  const awsConnectState = useAppSelector((state) => state?.appState)
  const dispatch = useAppDispatch()

  // const [parentKeyVal, setParentKeyVal] = useState<string>()
  if (element.elementName === componentNames.SINGLE_LOOKUP) {
    keyName = `lookupLabels.${element.keyName}`
  }
  if (element.elementName === componentNames.CATEGORY) {
    keyName = `lookupLabels.${element.keyName}`
  }
  // works because of control from form context
  const value = useWatch({ name: keyName })
  const singleLookupValue = useWatch({ name: element?.keyName })
  const singleLookupId = useWatch({
    name: element?.parent ? `${element?.parent?.lookupKeyName}.id` : `__NULL__`,
  })
  const id = useWatch({ name: getSiblingElementKeyName(element, 'id') })
  const [isMigrate, setIsMigrated] = useState<boolean>(true)

  useEffect(() => {
    const isMigration = async () => {
      try {
        const migration = await getMigrationStatus(id)
        setIsMigrated(migration)
      } catch (err) {
        console.error(err)
      }
    }

    if (
      element?.elementName === componentNames.UNIQUE_ID &&
      element?.meta?.d_entity_id === layout?.entity_id &&
      element?.meta?.d_entity_id === 32 &&
      id
    ) {
      isMigration()
    }
  }, [element?.elementName, element?.meta?.d_entity_id, id, layout?.entity_id])

  const isHyperlink = () => {
    if (!value) return false

    const hyperlinkTypes = [
      componentNames.URL,
      componentNames.WEBSITE,
      componentNames.EMAIL,
      componentNames.UNIQUE_ID,
    ] as string[]

    if (element?.isCallable) {
      return true
    }

    if (isThisAParentCaseIdHyperLink(element) && singleLookupValue?.id) {
      return true
    }

    if (
      element?.elementName === componentNames.UNIQUE_ID &&
      layout?.default_value &&
      element?.meta?.d_entity_id === layout?.entity_id &&
      !window?.location?.pathname?.includes('/workflow/review/') &&
      !window?.location?.pathname?.includes('interaction')
    ) {
      return false
    }

    if (
      element?.elementName === componentNames.UNIQUE_ID &&
      element?.meta?.d_entity_id === layout?.entity_id &&
      element?.meta?.d_entity_id === 32 &&
      id &&
      isMigrate &&
      !window?.location?.pathname?.includes('/workflow/review/') &&
      !window?.location?.pathname?.includes('interaction')
    ) {
      return false
    }

    return hyperlinkTypes.includes(element.elementName)
  }

  const onHyperlinkClick = () => {
    if (element.elementName === componentNames.EMAIL) {
      openWithTenant(`mailto:${value}`, '_blank')
    } else if (element?.isCallable) {
      if (
        awsConnectState?.isLoggedInToAWS &&
        !awsConnectState?.dialPad?.callInProgress
      ) {
        const phoneNumber = removePhoneMasking(value)
        dispatch(setDialPad({ number: phoneNumber }))
      } else if (!awsConnectState?.isLoggedInToAWS) {
        dispatch(
          addToaster({
            message: NAMESPACES.LOGIN_TO_CONTINUE,
            id: uuid(),
            type: 'error',
          })
        )
      } else if (awsConnectState?.dialPad?.callInProgress) {
        dispatch(
          addToaster({
            message: NAMESPACES?.CALL_IN_PROGRESS,
            id: uuid(),
            type: 'error',
          })
        )
      }
    } else if (element?.elementName === componentNames.SINGLE_LOOKUP) {
      openNewWindow(
        `/entity/${element?.meta?.d_entity_id}/workflow/review/${singleLookupValue?.id}`
      )
    } else if (element?.elementName === componentNames.UNIQUE_ID) {
      const entityId = element?.meta?.d_entity_id
      if (task?.identifier?.redirected_url) {
        openWithTenant(
          `/manage-queue/dynamic-queue/${task?.identifier?.redirected_url}`
        )
      } else {
        if (!isEmpty(element?.parent)) {
          if (!singleLookupId) {
            return
          }
          openNewWindow(
            `/${entitiesRoute}/${entityId}/${viewRoute}/${singleLookupId}`
          )
        } else openNewWindow(`/${entitiesRoute}/${entityId}/${viewRoute}/${id}`)
      }
    } else {
      openWithTenant(value, '_blank')
    }
  }

  // const checkParentKey = async () => {
  //   if (
  //     element.keyName === NAMESPACES.PARENT_CASE_ID &&
  //     !isEmpty(workflowDefinition)
  //   ) {
  //     const response = await getEntityDataByIdEmbedded(
  //       workflowDefinition?.global_variables?.entity_name ?? '',
  //       Number(workflowDefinition?.global_variables?.entity_id),
  //       [
  //         workflowDefinition?.global_variables?.config?.case_status ?? '',
  //         workflowDefinition?.global_variables?.config?.case_id ?? '',
  //       ],
  //       value || ''
  //     )
  //     if (response.data) {
  //       setParentKeyVal(response?.data?.case_key)
  //     }
  //   }
  // }

  // useEffect(() => {
  //   checkParentKey()
  // }, [element])

  if (element.elementName === componentNames.HEADER) {
    return (
      <div className="text-base font-medium font-primary min-h-10">
        {element.meta.label}
      </div>
    )
  } else if (element.elementName === componentNames.PARAGRAPH) {
    return (
      <div
        data-qa-id={element?.keyName || `paragraph-${layoutId}-${element?.id}`}
        className="text-large font-primary font-light min-h-10"
      >
        {element.meta.label}
      </div>
    )
  }
  const isDisabled =
    !!task?.identifier?.redirected_url &&
    (task?.state === TASK_STATUS.COMPLETED ||
      task?.state === TASK_STATUS.CANCELLED)

  return (
    <div
      className="min-h-10 px-2 break-words"
      data-qa-id={element?.keyName ?? element.meta.label ?? ''}
    >
      <Tooltip content={element.meta.tooltip_text ?? ''}>
        <div className="font-normal break-words text-sm">
          {element.meta.label}
          {element.meta?.required ? (
            <span
              className={`ml-1 text-xs leading-1  ${
                element.meta?.disabled ? 'text-accent-300' : 'text-error-500'
              }`}
            >
              *
            </span>
          ) : null}
        </div>
      </Tooltip>
      {isHyperlink() ? (
        <span
          className={`max-w-[20rem] block truncate text-xs font-extralight ${
            isDisabled
              ? 'text-black-500 cursor-not-allowed'
              : 'text-secondary-500 cursor-pointer'
          }`}
          onClick={() => {
            if (!isDisabled) {
              onHyperlinkClick()
            }
          }}
          tabIndex={isDisabled ? -1 : tabIndex ?? 0}
          onKeyDown={(e) => {
            if (
              !isDisabled &&
              (e?.key === ShortcutKeys.Enter || e?.key === ShortcutKeys?.Space)
            ) {
              onHyperlinkClick()
              e?.preventDefault()
            }
          }}
        >
          {value.toString() ?? ''}
        </span>
      ) : (
        <ShowComputedValue
          element={element}
          value={value}
          watchedValue={watchedValue}
        />
      )}
    </div>
  )
}

// const ShowHyperLink = ({
//   element,
//   value,
//   watchedValue,
//   onHyperlinkClick,
//   tabIndex,
//   parentKeyVal,
// }: ShowHyperLinkPropsType) => {
//   return element.keyName === NAMESPACES.PARENT_CASE_ID ? (
//     <>
//       <span
//         className={`max-w-[20rem] block truncate font-extralight text-secondary-500 cursor-pointer`}
//         onClick={onHyperlinkClick}
//         tabIndex={tabIndex ?? 0}
//         onKeyDown={(e) => {
//           if (e?.key === ShortcutKeys.Enter || e?.key === ShortcutKeys?.Space) {
//             onHyperlinkClick()
//             e?.preventDefault()
//           }
//         }}
//       >
//         {parentKeyVal ?? ''}
//       </span>
//       {/* TODO: if we have to dispplay underscore link then will use this
//     <a
//       className="text-blue-600 underline hover:text-blue-800"
//       href={`${window.location.origin}/entity/${
//         element?.meta?.d_entity_id
//       }/workflow/review/${Number(value)}`}
//       target="_blank"
//       rel="noopener noreferrer"
//       >
//       {parentKeyVal}
//     </a> */}
//     </>
//   ) : (
//     <ShowComputedValue
//       element={element}
//       value={value}
//       watchedValue={watchedValue}
//     />
//   )
// }
const ShowComputedValue = ({
  element,
  value,
  watchedValue,
}: ShowComputedValuePropsType) => {
  return element?.elementName === componentNames?.COMPUTED ? (
    <div className="font-extralight text-xs">
      {evaluateFormula(element?.meta?.formula ?? '', watchedValue)}
    </div>
  ) : (
    <div className="font-extralight text-xs">
      {formatValueForViewMode(element, value)?.toString()}
    </div>
  )
}

const LayoutRenderer: React.FC<LayoutRendererProps> = ({
  depth,
  layout,
  fullLayout,
  mode,
  data,
  formUniqueId,
  methods,
  parentFormKeyName,
  reregisterKey,
  hideAccordion,
  tabIndexMapForSubForm,
  subFormMetaObject,
  isStickySummary,
  fromIntakeReview,
  addTaskId,
  addProductIdFilterForPaysign,
  showPatientInformation,
  isTaskCompleted,
  subtotalkeys,
  handleSubtotalChange,
  task,
  layoutRowType,
}) => {
  const {
    // handleSubmit,
    setValue,
    register,
    control,
    // reset,
    formState: { errors },
    clearErrors,
    //watch,
    getValues,
  } = methods
  const [tabIndexmap, setTabIndexmap] = useState<TabIndexMap>({})
  const dataId = useWatch({
    name: parentFormKeyName ? parentFormKeyName + '.id' : 'id',
  })
  useEffect(() => {
    if (tabIndexMapForSubForm === undefined)
      setTabIndexmap(generateTabIndexMap(layout?.pages ?? []))
    else setTabIndexmap(tabIndexMapForSubForm ?? {})
  }, [layout, tabIndexMapForSubForm])
  if (depth && depth > SUPPORTED_NESTING_DEPTH) {
    // change it to 10 or 20 after we support nested subForm to avoid infinite rendering loops.
    return null // no nested subForm
  }

  if (depth === SUPPORTED_NESTING_DEPTH) {
    //remove when we support nested subForms / lookup / multilookup.
    // do not render threads OR MultiLookupRowRender inside a subForm.
    return layout.pages.map((page) => {
      return (
        <div key={page.pageId} className="page flex flex-col gap-2">
          {page.rows.map((row, idx) => {
            if (!row?.rowType && fullLayout) {
              return (
                <RowRenderer
                  row={row}
                  idx={idx}
                  key={row.rowId}
                  control={control}
                  errors={errors}
                  clearErrors={clearErrors}
                  register={register}
                  setValue={setValue}
                  mode={mode}
                  layout={layout}
                  data={data as FieldValues}
                  entityId={fullLayout?.entity_id.toString() ?? ''}
                  formUniqueId={formUniqueId}
                  reregisterKey={reregisterKey}
                  hideAccordion={hideAccordion}
                  tabIndexMap={
                    tabIndexmap?.[page?.pageId]?.[row?.rowId] ?? undefined
                  }
                  fullLayout={fullLayout}
                  dataId={dataId}
                  subFormMetaObject={subFormMetaObject}
                  isStickySummary={isStickySummary}
                  fromIntakeReview={fromIntakeReview}
                  showPatientInformation={showPatientInformation}
                  getValues={getValues}
                  task={task}
                />
              )
            }
            if (row?.rowType === ROW_TYPES.COMBOBOX) {
              return (
                <AdditionalNotes
                  key={row?.rowId}
                  name={row?.lookup_meta?.lookup_keyName ?? ''}
                  setValue={setValue}
                  control={control}
                  register={register}
                  label={row?.label}
                  mode={mode}
                  tabIndex={
                    tabIndexmap?.[page?.pageId]?.[row?.rowId]?.tabIndexOffset ??
                    undefined
                  }
                />
              )
            }
            if (row?.rowType === ROW_TYPES.SINGLE_LOOKUP_LISTING) {
              return null
            }
            return null
          })}
        </div>
      )
    })
  }

  return layout?.pages?.map((page) => {
    return (
      <div key={page?.pageId} className="page flex flex-col">
        {page.rows.map((row, idx) => {
          if (row?.rowType === ROW_TYPES.COMBOBOX) {
            return (
              <AdditionalNotes
                key={row?.rowId}
                name={row?.lookup_meta?.lookup_keyName ?? ''}
                setValue={setValue}
                control={control}
                register={register}
                label={row?.label}
                mode={mode}
                tabIndex={
                  tabIndexmap?.[page?.pageId]?.[row?.rowId]?.tabIndexOffset ??
                  undefined
                }
              />
            )
          }
          if (row?.rowType === ROW_TYPES.SINGLE_LOOKUP_LISTING) {
            return null
          }
          if (row?.rowType === ROW_TYPES.SUB_FORM) {
            return (
              <SubFormRenderer
                parentFormKeyName={parentFormKeyName}
                key={row.rowId}
                row={row}
                layout={fullLayout}
                mode={mode}
                methods={methods}
                isMultiLookup={
                  row?.lookup_meta?.type === componentNames.MULTI_LOOKUP
                }
                defaultExpandedCount={
                  row?.add_button_behavior ===
                  SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT.DISABLED_ADD
                    ? row?.initial_count
                    : 1
                }
                maxExpandedCount={row?.initial_count}
                reregisterKey={reregisterKey}
                formUniqueId={formUniqueId}
                depth={depth}
                tabOffset={
                  tabIndexmap?.[page?.pageId]?.[row?.rowId]?.tabIndexOffset
                }
                addProductIdFilterForPaysign={addProductIdFilterForPaysign}
                layoutRowType={layoutRowType}
              />
            )
          }
          return row?.rowType && fullLayout ? (
            <RenderNestedComponent
              data={data}
              dataId={dataId}
              formUniqueId={formUniqueId}
              fullLayout={fullLayout}
              mode={mode}
              page={page}
              register={register}
              row={row}
              tabIndexmap={tabIndexmap}
              addTaskId={addTaskId}
              isTaskCompleted={isTaskCompleted}
              task={task}
              parentFormKeyName={parentFormKeyName ?? ''}
              methods={methods}
              reregisterKey={reregisterKey ?? ''}
              subtotalkeys={subtotalkeys}
              handleSubtotalChange={handleSubtotalChange}
            />
          ) : (
            <RowRenderer
              row={row}
              idx={idx}
              key={row.rowId}
              control={control}
              errors={errors}
              clearErrors={clearErrors}
              register={register}
              setValue={setValue}
              mode={mode}
              layout={layout}
              data={data as FieldValues}
              entityId={fullLayout?.entity_id.toString() ?? ''}
              formUniqueId={formUniqueId}
              reregisterKey={reregisterKey}
              hideAccordion={hideAccordion}
              tabIndexMap={
                tabIndexmap?.[page?.pageId]?.[row?.rowId] ?? undefined
              }
              fullLayout={fullLayout}
              dataId={dataId}
              subFormMetaObject={subFormMetaObject}
              isStickySummary={isStickySummary}
              fromIntakeReview={fromIntakeReview}
              showPatientInformation={showPatientInformation}
              getValues={getValues}
              task={task}
              layoutRowType={layoutRowType}
            />
          )
        })}
      </div>
    )
  })
}

export const FormRenderer: React.FC<FormRendererProps> = ({
  layout,
  handleFormSubmit,
  heading,
  mode,
  data,
  handleFormInvalid,
  fullLayout,
  setIsFormDirty,
  footer,
  handleFormDraftSubmit,
  parentFormKeyName,
  reregisterKey,
  hideAccordion,
  tabIndexMap,
  subFormMetaObject,
  isStickySummary,
  isFromIntake,
  addTaskId,
  addProductIdFilterForPaysign,
  isTaskCompleted,
  showPatentInformation,
  handleInitForm,
  task,
  showMaxRTE = false,
  subtotalkeys,
  handleSubtotalChange,
  layoutRowType,
  ...rest
}: FormRendererProps) => {
  const methods = useForm({ reValidateMode: 'onBlur' })
  const {
    handleSubmit,
    setValue,
    // register,
    // control,
    watch,
    reset,
    formState: {
      // errors,
      isDirty,
    },
    clearErrors,
    getValues,
  } = rest.methods ?? methods
  const ownsFormInstance = !rest.methods
  const formInstanceId = useMemo(() => uuid(), [])
  const ancestryRegisteredRef = useRef(false)
  const targetRef = useRef<HTMLDivElement>(null)
  const shouldAutoScrollMaxRTE = useAppSelector(
    (state) => state?.maxRte.shouldAutoScroll
  )
  const dispatch = useAppDispatch()
  const layoutUid = fullLayout?.uid
  const formRules = fullLayout?.layout_data?.meta?.form_rules
  const hasFormRules = !!layoutUid && (formRules?.length ?? 0) > 0

  const formUniqueId = useMemo(() => {
    if (rest?.formUniqueId) return rest.formUniqueId
    return `lookup-modal-${uuid()}`
  }, [rest?.formUniqueId])

  const depth = rest?.depth ?? 0

  if (ownsFormInstance && fullLayout?.entity_name) {
    registerFormAncestryGetValues(formInstanceId, getValues)
    if (!ancestryRegisteredRef.current) {
      dispatch(
        registerFormInstance({
          instanceId: formInstanceId,
          entityName: fullLayout.entity_name,
          layoutUid: fullLayout.uid,
        })
      )
      ancestryRegisteredRef.current = true
    }
  }

  useEffect(() => {
    if (!ownsFormInstance || !fullLayout?.entity_name) return

    return () => {
      if (!ancestryRegisteredRef.current) return
      dispatch(unregisterFormInstance(formInstanceId))
      unregisterFormAncestryGetValues(formInstanceId)
      ancestryRegisteredRef.current = false
    }
  }, [ownsFormInstance, formInstanceId, fullLayout?.entity_name, dispatch])

  const headingRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(
    (targetRef: RefObject<HTMLDivElement>) => {
      if (targetRef.current) {
        const { top } = targetRef.current.getBoundingClientRect()
        if (top <= window.innerHeight - AUTO_SCROLL_OFFSET_PX) {
          dispatch(updateMaxRTEShouldAutoScroll(false))
          return
        }
        targetRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (!targetRef.current || !shouldAutoScrollMaxRTE) return
    const id = setInterval(
      () => handleScroll(targetRef),
      MAXRTE_AUTO_SCROLL_TIMEOUT
    )
    return () => clearInterval(id)
  }, [shouldAutoScrollMaxRTE, handleScroll])

  useEffect(() => {
    if (mode === MODE_CONSTANTS.ADD) {
      setDefaultValueForDropdown(layout?.added_fields ?? {}, setValue)
    }
  }, [layout, setValue, mode])

  useEffect(() => {
    if (data && Object.keys(data).length) {
      // preprocess
      hideUnauthorizedData(
        data,
        layout?.added_fields ?? {},
        fullLayout?.entity_id ?? '',
        false
      )

      formatDataBeforeLoad(data, layout?.added_fields ?? {})

      const prevValues = getValues()
      reset(merge(prevValues, data), { keepDirtyValues: true })

      if (hasFormRules && layoutUid) {
        triggerFormBehaviorEvaluation(layoutUid, 'hydrate')
      }
    }
  }, [data, reset, getValues, hasFormRules, layoutUid])

  useEffect(() => {
    if (hasFormRules && layoutUid && mode === MODE_CONSTANTS.ADD) {
      triggerFormBehaviorEvaluation(layoutUid, 'init')
    }
  }, [])

  useEffect(() => {
    setIsFormDirty?.(isDirty)
  }, [isDirty])

  useEffect(() => {
    handleInitForm?.(methods)
  }, [methods])

  // NOTE: (#Hardcoding) This is a coustom logic added for Income task add pop for calculating annual amount based on frequency and amount.
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name && ['poi_frequency', 'amount'].includes(name)) {
        const poiFrequency = values?.poi_frequency
        const amount = values?.amount
        if (poiFrequency && amount) {
          const annualAmount = calculateAnnualAmountFromFrequency(
            poiFrequency,
            amount
          )
          setValue('annual_amount', annualAmount)
        } else {
          setValue('annual_amount', 0)
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const injectScript = (url: string): HTMLScriptElement => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    document.body.appendChild(script)
    return script
  }

  const getUrlToInject = async (
    data: EntityLayout[]
  ): Promise<HTMLScriptElement | null> => {
    const path = data[0]?.document?.attachment?.[0]?.path
    const name = data[0]?.document?.attachment?.[0]?.name

    if (!path || !name) return null

    try {
      const url = await fetchDownloadUrlForSingleFIles(path, name)
      if (!url?.data) throw new Error('Invalid response')
      return injectScript(url.data?.url)
    } catch (error) {
      console.error(NAMESPACES.DOWNLOAD_ERROR, error)
      return null
    }
  }

  useEffect(() => {
    if (fullLayout) {
      let scriptElement: HTMLScriptElement | null = null
      const handleScriptInjection = async () => {
        scriptElement = await getUrlToInject([fullLayout])
      }
      handleScriptInjection()
      return () => {
        if (scriptElement?.parentNode) {
          scriptElement.parentNode.removeChild(scriptElement)
        }
      }
    }
  }, [fullLayout])

  if (rest.methods) {
    // parentFormKeyName will be used here only.
    const layoutContent = (
      <LayoutRenderer
        layout={layout}
        fullLayout={fullLayout}
        methods={rest.methods}
        mode={mode}
        data={data}
        formUniqueId={formUniqueId}
        parentFormKeyName={parentFormKeyName}
        reregisterKey={reregisterKey}
        depth={depth}
        hideAccordion={hideAccordion}
        tabIndexMapForSubForm={tabIndexMap}
        subFormMetaObject={subFormMetaObject}
        isStickySummary={isStickySummary}
        fromIntakeReview={isFromIntake}
        addProductIdFilterForPaysign={addProductIdFilterForPaysign}
        showPatientInformation={showPatentInformation}
        task={task}
        subtotalkeys={subtotalkeys}
        handleSubtotalChange={handleSubtotalChange}
        layoutRowType={layoutRowType}
      />
    )

    if (hasFormRules && formRules && layoutUid) {
      return (
        <FormBehaviorRunner
          layoutUid={layoutUid}
          rules={formRules}
          setValue={rest.methods.setValue}
          getValues={rest.methods.getValues}
          reset={rest.methods.reset}
          formInstanceId={ownsFormInstance ? formInstanceId : undefined}
          formUniqueId={formUniqueId}
          entityName={fullLayout?.entity_name}
        >
          {layoutContent}
        </FormBehaviorRunner>
      )
    }

    return layoutContent
  }

  const formContent = (
    <form
      onSubmit={(e) => {
        e.stopPropagation()
        e.preventDefault()
        const embedded = Object.keys(getValues()?.['subForm'] ?? {})
        if (isDraftSubmission(e)) {
          e.preventDefault()
          const { formattedValues, originalFormData } =
            removeAuditFieldsAndFormatValues(getValues(), layout, mode, data)

          handleFormDraftSubmit?.(formattedValues, embedded, originalFormData)
          clearErrors()
          return
        }

        handleSubmit((values) => {
          const embedded = Object.keys(values?.['subForm'] ?? {})
          const { formattedValues, originalFormData } =
            removeAuditFieldsAndFormatValues(values, layout, mode, data)
          if (addTaskId)
            formattedValues['task'] = {
              id: addTaskId,
            }
          handleFormSubmit(formattedValues, embedded, originalFormData)
        }, handleFormInvalid)(e)
      }}
    >
      <button
        type="submit"
        disabled
        tabIndex={-1}
        style={{ display: 'none' }}
        aria-hidden="true"
      ></button>
      {<div ref={headingRef}>{heading}</div>}
      <LayoutRenderer
        fullLayout={fullLayout}
        layout={layout}
        methods={methods}
        mode={mode}
        data={data}
        formUniqueId={formUniqueId}
        reregisterKey={reregisterKey}
        depth={depth}
        hideAccordion={hideAccordion}
        tabIndexMapForSubForm={tabIndexMap}
        subFormMetaObject={subFormMetaObject}
        isStickySummary={isStickySummary}
        fromIntakeReview={isFromIntake}
        addTaskId={addTaskId}
        addProductIdFilterForPaysign={addProductIdFilterForPaysign}
        isTaskCompleted={isTaskCompleted}
        showPatientInformation={showPatentInformation}
        task={task}
        subtotalkeys={subtotalkeys}
        handleSubtotalChange={handleSubtotalChange}
        layoutRowType={layoutRowType}
      />
      {(showMaxRTE ||
        MAXRTE_SUPPORTED_TABLE_LAYOUT_IDS.includes(fullLayout?.id)) && (
        <FormHeaderForMaxRTE
          ref={targetRef}
          mode={mode}
          isFormDirty={true}
          layout={fullLayout?.layout_data}
          dataId={data?.id}
        />
      )}
      {<div>{footer}</div>}
    </form>
  )

  return (
    <div className="layout">
      <div id={formUniqueId} />
      <FormProvider {...methods}>
        {hasFormRules && formRules && layoutUid ? (
          <FormBehaviorRunner
            layoutUid={layoutUid}
            rules={formRules}
            setValue={methods.setValue}
            getValues={methods.getValues}
            reset={methods.reset}
            formInstanceId={ownsFormInstance ? formInstanceId : undefined}
            formUniqueId={formUniqueId}
            entityName={fullLayout?.entity_name}
          >
            {formContent}
          </FormBehaviorRunner>
        ) : (
          formContent
        )}
      </FormProvider>
    </div>
  )
}

export default FormRenderer

const RenderNestedComponent = ({
  row,
  data,
  fullLayout,
  dataId,
  mode,
  register,
  formUniqueId,
  page,
  tabIndexmap,
  addTaskId,
  isTaskCompleted,
  task,
  parentFormKeyName,
  methods,
  reregisterKey,
  subtotalkeys,
  handleSubtotalChange,
}: RenderNestedComponentPropsType) => {
  return row?.rowType === ROW_TYPES.NOTES || row?.rowType === ROW_TYPES.NOTE ? (
    <ThreadsNotesAccordion
      isRequired={data?.meta?.required as boolean}
      relationIdentifier={
        fullLayout?.layout_data?.added_fields?.[
          row?.lookup_meta?.lookup_keyName ?? ''
        ]?.relationship?.relation_identifier ?? ''
      }
      row={row}
      layoutData={fullLayout}
      id={dataId}
      mode={mode}
      register={register}
      name={
        fullLayout?.layout_data?.added_fields?.[
          row?.lookup_meta?.lookup_keyName ?? ''
        ]?.name ?? undefined
      }
      createLayoutId={
        fullLayout?.layout_data?.added_fields?.[
          row?.lookup_meta?.lookup_keyName ?? ''
        ]?.create_layout_id ?? undefined
      }
      entityId={fullLayout?.entity_id?.toString()}
      entityName={fullLayout?.entity_name}
      tabIndex={tabIndexmap?.[page?.pageId]?.[row?.rowId]?.tabIndexOffset}
    />
  ) : mode === MODE_CONSTANTS?.ADD &&
    (row?.lookup_meta?.lookup_keyName === NAMESPACES.C_PATIENT_ADDRESS ||
      row?.lookup_meta?.lookup_keyName === NAMESPACES.C_PATIENT_PHONE ||
      row?.lookup_meta?.lookup_keyName === NAMESPACES.C_CONTACT_PHONE ||
      row?.lookup_meta?.lookup_keyName === NAMESPACES.C_CONTACT_ADDRESS ||
      row?.lookup_meta?.lookup_keyName === NAMESPACES.C_PROVIDER_CONTACT) ? (
    <SubFormRenderWrapper
      parentFormKeyName={parentFormKeyName}
      methods={methods}
      mode={mode}
      reregisterKey={reregisterKey}
      formUniqueId={formUniqueId ?? ''}
      row={row}
      fullLayout={fullLayout}
    />
  ) : (
    <RowRender
      row={row}
      layout={fullLayout}
      dataId={dataId}
      formUniqueId={formUniqueId}
      parentRecordStatus={data?.['record_status']}
      tabIndexMap={tabIndexmap?.[page?.pageId]?.[row?.rowId] ?? undefined}
      addTaskId={addTaskId}
      isTaskCompleted={isTaskCompleted}
      task={task}
      fullLayout={fullLayout}
      mode={mode}
      handleSubtotalChange={handleSubtotalChange}
      subtotalkeys={subtotalkeys}
      showSubTotal={!!subtotalkeys}
    />
  )
}
